// @ts-strict-ignore
import * as React from 'react';
import GridLayout, { Layout } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

const GRID_COLS = 4;
const ORG_OVERVIEW_GRID_LAYOUTS_STORAGE_KEY = 'org-overview-grid-layouts';

export interface OrgOverviewGridWidget {
    id: string;
    content: React.ReactNode;
    x?: number;
    y?: number;
    w?: number;
    h?: number;
    minW?: number;
    minH?: number;
}

function buildDefaultLayout(widgets: OrgOverviewGridWidget[]): Layout[] {
    let nextX = 0;
    let nextY = 0;
    let currentRowHeight = 0;

    return widgets.map((widget) => {
        const w = widget.w ?? 1;
        const h = widget.h ?? 3;

        if (widget.x == null || widget.y == null) {
            if (nextX + w > GRID_COLS) {
                nextX = 0;
                nextY += currentRowHeight;
                currentRowHeight = 0;
            }

            const layoutItem = {
                i: widget.id,
                x: widget.x ?? nextX,
                y: widget.y ?? nextY,
                w,
                h,
                minW: widget.minW ?? 1,
                minH: widget.minH ?? 3,
            };

            nextX += w;
            currentRowHeight = Math.max(currentRowHeight, h);

            return layoutItem;
        }

        return {
            i: widget.id,
            x: widget.x,
            y: widget.y,
            w,
            h,
            minW: widget.minW ?? 1,
            minH: widget.minH ?? 3,
        };
    });
}

function normalizeLayoutItem(item: Layout): Layout {
    const normalizedWidth = Math.max(1, Math.min(item.w, GRID_COLS));
    const normalizedMinWidth = Math.max(1, Math.min(item.minW ?? 1, normalizedWidth));

    return {
        ...item,
        w: normalizedWidth,
        minW: normalizedMinWidth,
        x: Math.max(0, Math.min(item.x, GRID_COLS - normalizedWidth)),
    };
}

function layoutsOverlap(left: Layout, right: Layout): boolean {
    return (
        left.x < right.x + right.w &&
        left.x + left.w > right.x &&
        left.y < right.y + right.h &&
        left.y + left.h > right.y
    );
}

function compactLayout(layout: Layout[]): Layout[] {
    const sortedLayout = [...layout]
        .map(normalizeLayoutItem)
        .sort((left, right) => (left.y === right.y ? left.x - right.x : left.y - right.y));
    const compacted: Layout[] = [];

    sortedLayout.forEach((item) => {
        let nextY = 0;
        let placedItem: Layout | null = null;

        while (!placedItem) {
            for (let nextX = 0; nextX <= GRID_COLS - item.w; nextX += 1) {
                const candidate = normalizeLayoutItem({
                    ...item,
                    x: nextX,
                    y: nextY,
                });

                if (compacted.every((existingItem) => !layoutsOverlap(candidate, existingItem))) {
                    placedItem = candidate;
                    compacted.push(candidate);
                    break;
                }
            }

            nextY += 1;
        }
    });

    return compacted;
}

function mergeLayout(previous: Layout[], next: Layout[]) {
    const previousById = new Map(previous.map((item) => [item.i, item]));

    const mergedLayout = next.map((item) => {
        const existing = previousById.get(item.i);
        if (!existing) {
            return normalizeLayoutItem(item);
        }

        return normalizeLayoutItem({
            ...item,
            x: existing.x,
            y: existing.y,
            w: existing.w,
            h: existing.h,
        });
    });

    return compactLayout(mergedLayout);
}

function getStoredLayouts(): Record<string, Layout[]> {
    try {
        const rawValue = window.localStorage.getItem(ORG_OVERVIEW_GRID_LAYOUTS_STORAGE_KEY);

        if (!rawValue) {
            return {};
        }

        return JSON.parse(rawValue) as Record<string, Layout[]>;
    } catch {
        return {};
    }
}

function getStoredLayout(storageKey?: string): Layout[] | null {
    if (!storageKey) {
        return null;
    }

    const storedLayouts = getStoredLayouts();
    return storedLayouts[storageKey] ?? null;
}

function setStoredLayout(storageKey: string, layout: Layout[]): void {
    try {
        const storedLayouts = getStoredLayouts();
        const existingLayout = storedLayouts[storageKey] ?? [];
        const nextLayoutById = new Map(layout.map((item) => [item.i, item]));
        const preservedItems = existingLayout.filter((item) => !nextLayoutById.has(item.i));

        window.localStorage.setItem(
            ORG_OVERVIEW_GRID_LAYOUTS_STORAGE_KEY,
            JSON.stringify({
                ...storedLayouts,
                [storageKey]: [...layout, ...preservedItems],
            }),
        );
    } catch {
        return;
    }
}

export const OrgOverviewGridSection: React.FC<{
    widgets: OrgOverviewGridWidget[];
    storageKey?: string;
}> = ({ widgets, storageKey }) => {
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const defaultLayout = React.useMemo(() => buildDefaultLayout(widgets), [widgets]);
    const [layout, setLayout] = React.useState<Layout[]>(() => {
        const storedLayout = getStoredLayout(storageKey);
        return storedLayout ? mergeLayout(storedLayout, defaultLayout) : defaultLayout;
    });
    const [width, setWidth] = React.useState(0);
    const [isDragging, setIsDragging] = React.useState(false);
    const [isResizing, setIsResizing] = React.useState(false);
    const widgetIdsSignature = React.useMemo(() => widgets.map((widget) => widget.id).join('|'), [widgets]);
    const previousWidgetIdsSignatureRef = React.useRef(widgetIdsSignature);

    React.useEffect(() => {
        if (previousWidgetIdsSignatureRef.current !== widgetIdsSignature) {
            previousWidgetIdsSignatureRef.current = widgetIdsSignature;
            const storedLayout = getStoredLayout(storageKey);
            setLayout(storedLayout ? mergeLayout(storedLayout, defaultLayout) : defaultLayout);
            return;
        }

        setLayout((current) => mergeLayout(current, defaultLayout));
    }, [defaultLayout, storageKey, widgetIdsSignature]);

    React.useEffect(() => {
        if (!storageKey) {
            return;
        }

        setStoredLayout(storageKey, layout);
    }, [layout, storageKey]);

    React.useEffect(() => {
        if (!containerRef.current) {
            return undefined;
        }

        const updateWidth = () => {
            if (!containerRef.current) {
                return;
            }

            setWidth(containerRef.current.getBoundingClientRect().width);
        };

        updateWidth();

        const observer = new ResizeObserver(() => {
            updateWidth();
        });

        observer.observe(containerRef.current);

        return () => {
            observer.disconnect();
        };
    }, []);

    return (
        <div ref={containerRef} style={{ width: '100%' }}>
            {width > 0 && (
                <GridLayout
                    className={`layout org-overview-grid-layout${isDragging ? ' org-overview-grid-layout--dragging' : ''}${isResizing ? ' org-overview-grid-layout--resizing' : ''}`}
                    cols={GRID_COLS}
                    width={width}
                    rowHeight={72}
                    margin={[20, 20]}
                    containerPadding={[0, 0]}
                    layout={layout}
                    onLayoutChange={setLayout}
                    onDragStart={() => setIsDragging(true)}
                    onDragStop={() => setIsDragging(false)}
                    onResizeStart={() => setIsResizing(true)}
                    onResizeStop={() => setIsResizing(false)}
                    isDraggable
                    isResizable
                    resizeHandles={['e', 's', 'se']}
                    useCSSTransforms
                >
                    {widgets.map((widget) => (
                        <div key={widget.id}>
                            <div
                                style={{
                                    height: '100%',
                                    minHeight: 0,
                                }}
                            >
                                {widget.content}
                            </div>
                        </div>
                    ))}
                </GridLayout>
            )}
        </div>
    );
};
